// src/pages/blog/BlogTag.js
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  ChevronRight,
  Eye,
  Tag as TagIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { useConfig } from "../../context/ConfigContext";
import { getImageUrl } from "../../services/axiosConfig.ts";
import axios from "../../utils/axiosHandler";

const BlogTag = () => {
  const { slug } = useParams();
  const { config } = useConfig(); // Get config from context
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tag, setTag] = useState(null);
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    try {
      // Set the document title using the site name from config
      document.title = "Blog | " + config?.siteName;
    } catch (err) {
      console.error("Error fetching page:", err);
    }
  }, [config?.siteName]); // Add config.siteName to the dependency array

  useEffect(() => {
    fetchTagAndPosts();
  }, [slug]);

  useEffect(() => {
    if (tag) {
      fetchPosts(currentPage);
    }
  }, [tag, currentPage]);

  const fetchTagAndPosts = async () => {
    setLoading(true);
    try {
      // Fetch all tags to find the one we need
      const tagsRes = await axios.get("/api/blog/tags");

      const tagsData = await tagsRes.data;
      const currentTag = tagsData.find((t) => t.slug === slug);

      if (!currentTag) {
        throw new Error("Tag not found");
      }

      setTag(currentTag);
      fetchPosts(1);

      setError(null);
    } catch (err) {
      console.error("Error fetching tag:", err);
      setError(err.message || "Failed to load the tag");
      setLoading(false);
    }
  };

  const fetchPosts = async (page) => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        page,
        limit: 9,
        sort: "newest",
      });

      const res = await axios.get(
        `/api/blog/posts?tag=${slug}&${params.toString()}`
      );
      const data = await res.data;
      setPosts(data.posts);
      setTotalPages(data.totalPages);

      setError(null);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError("Failed to load posts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading && !tag) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-20">
          <div
            className="spinner mx-auto mb-4"
            style={{ borderTopColor: config.colors.primary }}
          ></div>
          <p className="text-gray-600">Loading tag...</p>
        </div>
      </div>
    );
  }

  if (error && !tag) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-red-100 text-red-800 p-6 rounded-lg">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 mr-2" />
              <h2 className="text-xl font-bold">Error</h2>
            </div>
            <p>{error}</p>
            <Link
              to="/blog"
              className="mt-4 flex items-center hover:text-blue-800"
              style={{ color: config.colors.primary }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = `${config.colors.primary}DD`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = config.colors.primary;
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!tag) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gray-100 text-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Tag not found</h2>
            <p>
              The tag you are looking for does not exist or has been removed.
            </p>
            <Link
              to="/blog"
              className="mt-4 flex items-center hover:text-blue-800"
              style={{ color: config.colors.primary }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = `${config.colors.primary}DD`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = config.colors.primary;
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>{tag.name + " |" + config.siteName}</title>
        <meta
          name="description"
          content={`Posts tagged with ${tag.name} - ${config?.siteName || "BagBox"
            }`}
        />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <nav className="flex items-center text-sm text-gray-500">
            <Link
              to="/"
              className="hover:text-blue-600"
              style={{ ":hover": { color: config.colors.primary } }}
            >
              Home
            </Link>
            <span className="mx-2">&#8250;</span>
            <Link
              to="/blog"
              className="hover:text-blue-600"
              style={{ ":hover": { color: config.colors.primary } }}
            >
              Blog
            </Link>
            <span className="mx-2">&#8250;</span>
            <span className="text-gray-700">Tag: {tag.name}</span>
          </nav>
        </div>

        {/* Tag Header */}
        <div className="mb-10">
          <div className="flex items-center justify-center">
            <TagIcon
              className="h-6 w-6 mr-2"
              style={{ color: config.colors.textSecondary || "#6B7280" }}
            />
            <h1
              className="text-3xl md:text-4xl font-bold text-center"
              style={{ color: config.colors.textPrimary || "#111827" }}
            >
              #{tag.name}
            </h1>
          </div>
          <p
            className="text-center mt-2"
            style={{ color: config.colors.textSecondary || "#6B7280" }}
          >
            Showing all posts tagged with "{tag.name}"
          </p>
        </div>

        {error && (
          <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-8">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Posts Grid */}
        {loading && !posts.length ? (
          <div className="text-center py-20">
            <div
              className="spinner mx-auto mb-4"
              style={{ borderTopColor: config.colors.primary }}
            ></div>
            <p className="text-gray-600">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-gray-50 rounded-lg">
            <p className="text-gray-500 mb-4">No posts found with this tag</p>
            <Link
              to="/blog"
              className="hover:text-blue-800"
              style={{ color: config.colors.primary }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = `${config.colors.primary}DD`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = config.colors.primary;
              }}
            >
              Back to Blog
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <div
                  key={post._id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="h-48 bg-gray-200">
                      {(post.coverImage || post.featuredImage?.url) ? (
                        <img
                          src={getImageUrl(post.coverImage || post.featuredImage?.url)}
                          alt={post.featuredImage?.alt || post.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-gray-400">No image</span>
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="p-6">
                    <Link
                      to={`/blog/category/${post.category.slug}`}
                      className="inline-block text-xs px-2 py-1 rounded-full mb-2"
                      style={{
                        backgroundColor: `${config.colors.primary}20`,
                        color: `${config.colors.primary}E6`,
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = `${config.colors.primary}30`;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = `${config.colors.primary}20`;
                      }}
                    >
                      {post.category.name}
                    </Link>
                    <h3 className="text-xl font-bold mb-2 line-clamp-2">
                      <Link
                        to={`/blog/${post.slug}`}
                        style={{
                          color: config.colors.textPrimary || "#111827",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = config.colors.primary;
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color =
                            config.colors.textPrimary || "#111827";
                        }}
                      >
                        {post.title}
                      </Link>
                    </h3>
                    <div className="flex items-center mb-2 text-sm text-gray-500">
                      <Calendar
                        className="h-4 w-4 mr-1"
                        style={{ color: `${config.colors.primary}AA` }}
                      />
                      {formatDate(post.publishedAt || post.createdAt)}
                      <span className="mx-2">•</span>
                      <Eye
                        className="h-4 w-4 mr-1"
                        style={{ color: `${config.colors.primary}AA` }}
                      />
                      {post.viewCount} views
                    </div>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="flex items-center text-sm font-medium"
                      style={{ color: config.colors.primary }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.color = `${config.colors.primary}DD`;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.color = config.colors.primary;
                      }}
                    >
                      Read More
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-10 flex justify-center">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded ${currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded flex items-center justify-center ${currentPage === page
                          ? "text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                          }`}
                        style={{
                          backgroundColor:
                            currentPage === page ? config.colors.primary : "",
                        }}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded ${currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* CSS for spinner and line clamp */}
      <style jsx>{`
        .spinner {
          border: 3px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          border-top: 3px solid ${config.colors.primary || "#4f46e5"};
          width: 24px;
          height: 24px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default BlogTag;

// // before colorimg from config context
// // src/pages/blog/BlogTag.js
// import React, { useState, useEffect } from 'react';
// import { useParams, Link } from 'react-router-dom';
// import { Calendar, Eye, ChevronRight, AlertCircle, ArrowLeft, Tag as TagIcon } from 'lucide-react';
// import { Helmet } from 'react-helmet';

// const BlogTag = () => {
//   const { slug } = useParams();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [tag, setTag] = useState(null);
//   const [posts, setPosts] = useState([]);
//   const [totalPages, setTotalPages] = useState(1);
//   const [currentPage, setCurrentPage] = useState(1);

//   useEffect(() => {
//     fetchTagAndPosts();
//   }, [slug]);

//   useEffect(() => {
//     if (tag) {
//       fetchPosts(currentPage);
//     }
//   }, [tag, currentPage]);

//   const fetchTagAndPosts = async () => {
//     setLoading(true);
//     try {
//       // Fetch all tags to find the one we need
//       const tagsRes = await fetch('/api/blog/tags');

//       if (!tagsRes.ok) {
//         throw new Error('Failed to fetch tags');
//       }

//       const tagsData = await tagsRes.json();
//       const currentTag = tagsData.find(t => t.slug === slug);

//       if (!currentTag) {
//         throw new Error('Tag not found');
//       }

//       setTag(currentTag);
//       fetchPosts(1);

//       setError(null);
//     } catch (err) {
//       console.error('Error fetching tag:', err);
//       setError(err.message || 'Failed to load the tag');
//       setLoading(false);
//     }
//   };

//   const fetchPosts = async (page) => {
//     setLoading(true);
//     try {
//       // Build query params
//       const params = new URLSearchParams({
//         page,
//         limit: 9,
//         sort: 'newest'
//       });

//       const res = await fetch(`/api/blog/posts?tag=${slug}&${params.toString()}`);

//       if (!res.ok) throw new Error('Failed to fetch posts');

//       const data = await res.json();
//       setPosts(data.posts);
//       setTotalPages(data.totalPages);

//       setError(null);
//     } catch (err) {
//       console.error('Error fetching posts:', err);
//       setError('Failed to load posts. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Format date
//   const formatDate = (dateString) => {
//     const options = { year: 'numeric', month: 'long', day: 'numeric' };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   };

//   if (loading && !tag) {
//     return (
//       <div className="container mx-auto px-4 py-8">
//         <div className="text-center py-20">
//           <div className="spinner mx-auto mb-4"></div>
//           <p className="text-gray-600">Loading tag...</p>
//         </div>
//       </div>
//     );
//   }

//   if (error && !tag) {
//     return (
//       <div className="container mx-auto px-4 py-8">
//         <div className="max-w-3xl mx-auto">
//           <div className="bg-red-100 text-red-800 p-6 rounded-lg">
//             <div className="flex items-center mb-4">
//               <AlertCircle className="h-6 w-6 mr-2" />
//               <h2 className="text-xl font-bold">Error</h2>
//             </div>
//             <p>{error}</p>
//             <Link
//               to="/blog"
//               className="mt-4 flex items-center text-blue-600 hover:text-blue-800"
//             >
//               <ArrowLeft className="h-4 w-4 mr-1" />
//               Back to Blog
//             </Link>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (!tag) {
//     return (
//       <div className="container mx-auto px-4 py-8">
//         <div className="max-w-3xl mx-auto">
//           <div className="bg-gray-100 text-gray-800 p-6 rounded-lg">
//             <h2 className="text-xl font-bold mb-4">Tag not found</h2>
//             <p>The tag you are looking for does not exist or has been removed.</p>
//             <Link
//               to="/blog"
//               className="mt-4 flex items-center text-blue-600 hover:text-blue-800"
//             >
//               <ArrowLeft className="h-4 w-4 mr-1" />
//               Back to Blog
//             </Link>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="container mx-auto px-4 py-8">

//       <Helmet>
//         <title>{tag.name} | BagBox</title>
//         <meta name="description" content={tag.name} />
//       </Helmet>

//       <div className="max-w-6xl mx-auto">
//         {/* Breadcrumb */}
//         <div className="mb-6">
//           <nav className="flex items-center text-sm text-gray-500">
//             <Link to="/" className="hover:text-blue-600">Home</Link>
//             <span className="mx-2">&#8250;</span>
//             <Link to="/blog" className="hover:text-blue-600">Blog</Link>
//             <span className="mx-2">&#8250;</span>
//             <span className="text-gray-700">Tag: {tag.name}</span>
//           </nav>
//         </div>

//         {/* Tag Header */}
//         <div className="mb-10">
//           <div className="flex items-center justify-center">
//             <TagIcon className="h-6 w-6 mr-2 text-gray-600" />
//             <h1 className="text-3xl md:text-4xl font-bold text-center">#{tag.name}</h1>
//           </div>
//           <p className="text-center text-gray-600 mt-2">
//             Showing all posts tagged with "{tag.name}"
//           </p>
//         </div>

//         {error && (
//           <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-8">
//             <div className="flex items-center space-x-2">
//               <AlertCircle className="h-5 w-5" />
//               <span>{error}</span>
//             </div>
//           </div>
//         )}

//         {/* Posts Grid */}
//         {loading && !posts.length ? (
//           <div className="text-center py-20">
//             <div className="spinner mx-auto mb-4"></div>
//             <p className="text-gray-600">Loading posts...</p>
//           </div>
//         ) : posts.length === 0 ? (
//           <div className="text-center py-20 bg-gray-50 rounded-lg">
//             <p className="text-gray-500 mb-4">No posts found with this tag</p>
//             <Link
//               to="/blog"
//               className="text-blue-600 hover:text-blue-800"
//             >
//               Back to Blog
//             </Link>
//           </div>
//         ) : (
//           <>
//             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//               {posts.map(post => (
//                 <div key={post._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
//                   <Link to={`/blog/${post.slug}`}>
//                     <div className="h-48 bg-gray-200">
//                       {post.coverImage ? (
//                         <img
//                           src={post.coverImage}
//                           alt={post.title}
//                           className="w-full h-full object-cover"
//                         />
//                       ) : (
//                         <div className="w-full h-full flex items-center justify-center">
//                           <span className="text-gray-400">No image</span>
//                         </div>
//                       )}
//                     </div>
//                   </Link>
//                   <div className="p-6">
//                     <Link
//                       to={`/blog/category/${post.category.slug}`}
//                       className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mb-2 hover:bg-blue-200"
//                     >
//                       {post.category.name}
//                     </Link>
//                     <h3 className="text-xl font-bold mb-2 hover:text-blue-600 line-clamp-2">
//                       <Link to={`/blog/${post.slug}`}>
//                         {post.title}
//                       </Link>
//                     </h3>
//                     <div className="flex items-center mb-2 text-sm text-gray-500">
//                       <Calendar className="h-4 w-4 mr-1" />
//                       {formatDate(post.publishedAt || post.createdAt)}
//                       <span className="mx-2">•</span>
//                       <Eye className="h-4 w-4 mr-1" />
//                       {post.viewCount} views
//                     </div>
//                     <p className="text-gray-600 mb-4 line-clamp-3">
//                       {post.excerpt}
//                     </p>
//                     <Link
//                       to={`/blog/${post.slug}`}
//                       className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium"
//                     >
//                       Read More
//                       <ChevronRight className="h-4 w-4" />
//                     </Link>
//                   </div>
//                 </div>
//               ))}
//             </div>

//             {/* Pagination */}
//             {totalPages > 1 && (
//               <div className="mt-10 flex justify-center">
//                 <div className="flex items-center space-x-2">
//                   <button
//                     onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//                     disabled={currentPage === 1}
//                     className={`px-3 py-1 rounded ${
//                       currentPage === 1
//                         ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
//                         : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//                     }`}
//                   >
//                     Previous
//                   </button>

//                   {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
//                     <button
//                       key={page}
//                       onClick={() => setCurrentPage(page)}
//                       className={`w-8 h-8 rounded flex items-center justify-center ${
//                         currentPage === page
//                           ? 'bg-blue-600 text-white'
//                           : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//                       }`}
//                     >
//                       {page}
//                     </button>
//                   ))}

//                   <button
//                     onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//                     disabled={currentPage === totalPages}
//                     className={`px-3 py-1 rounded ${
//                       currentPage === totalPages
//                         ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
//                         : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//                     }`}
//                   >
//                     Next
//                   </button>
//                 </div>
//               </div>
//             )}
//           </>
//         )}
//       </div>

//       {/* CSS for spinner and line clamp */}
//       <style jsx>{`
//         .spinner {
//           border: 3px solid rgba(0, 0, 0, 0.1);
//           border-radius: 50%;
//           border-top: 3px solid #4f46e5;
//           width: 24px;
//           height: 24px;
//           animation: spin 1s linear infinite;
//         }

//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }

//         .line-clamp-2 {
//           display: -webkit-box;
//           -webkit-line-clamp: 2;
//           -webkit-box-orient: vertical;
//           overflow: hidden;
//         }

//         .line-clamp-3 {
//           display: -webkit-box;
//           -webkit-line-clamp: 3;
//           -webkit-box-orient: vertical;
//           overflow: hidden;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default BlogTag;
